import type { TaskFilters } from '@/store/task-filters.types'
import { tasksApi } from '@/lib/api/tasks.api'
import { createLogger } from '@/lib/logger'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  ModuleTypeEnum,
  PaginatedResponse,
  PaginationParams,
  TaskWithAssignments,
} from '@scrambl/shared'
import { isValidUUID } from '@scrambl/shared'

import { queryKeys } from '../query-keys'

const logger = createLogger('hooks/queries/useTasksQuery')

interface TasksResponse extends PaginatedResponse<TaskWithAssignments> {}

/**
 * Hook to fetch tasks with filters
 * Filters can be passed directly or come from Zustand store
 * Server data is managed by TanStack Query
 */
export function useTasksQuery(
  filters?: TaskFilters,
  options?: {
    enabled?: boolean
    view?: 'minimal' | 'list' | 'board' | 'full'
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<TasksResponse>({
    queryKey: [
      ...queryKeys.tasks.list(filters as Record<string, unknown>),
      { source: 'api' },
    ],
    queryFn: async () => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const fetchApiTasks = async () => {
        // Convert TaskFilters to API params
        const params: PaginationParams & Record<string, unknown> = {
          page: filters?.page || 1,
          limit: filters?.limit || 50,
          sortBy: filters?.sortBy,
          sortOrder: filters?.sortOrder,
        }

        // Add optional filters
        if (filters?.moduleType) params.moduleType = filters.moduleType
        if (filters?.moduleId) params.moduleId = filters.moduleId
        if (filters?.groupId) params.groupId = filters.groupId
        if (filters?.groupIds && filters.groupIds.length > 0) {
          params.groupIds = filters.groupIds
        }
        if (filters?.sprintId) params.sprintId = filters.sprintId
        if (filters?.search) params.search = filters.search
        if (filters?.assigneeIds && filters.assigneeIds.length > 0) {
          params.assigneeIds = filters.assigneeIds
        }
        if (filters?.statuses && filters.statuses.length > 0) {
          params.statuses = filters.statuses
        }
        if (filters?.taskTypeDefIds && filters.taskTypeDefIds.length > 0) {
          params.taskTypeDefIds = filters.taskTypeDefIds
        }
        if (filters?.dueDateFrom) params.dueDateFrom = filters.dueDateFrom
        if (filters?.dueDateTo) params.dueDateTo = filters.dueDateTo
        if (filters?.scheduledForFrom)
          params.scheduledForFrom = filters.scheduledForFrom
        if (filters?.scheduledForTo)
          params.scheduledForTo = filters.scheduledForTo
        if (filters?.excludeCompleted !== undefined) {
          params.excludeCompleted = filters.excludeCompleted
        }
        if (filters?.includePersonalTasks !== undefined) {
          params.includePersonalTasks = filters.includePersonalTasks
        }

        // Add view option
        if (options?.view) {
          params.view = options.view
        }

        // Merge custom params if provided
        if (filters?.customParams) {
          Object.assign(params, filters.customParams)
        }

        return tasksApi.getTasks(params)
      }

      return fetchApiTasks()
    },
    enabled: options?.enabled !== false && !!currentOrganization?.id,
    placeholderData: previous => previous,
    staleTime: 60 * 1000, // 1 minute - tasks can change frequently
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  })
}

/**
 * Hook to fetch a single task by ID
 */
export function useTaskQuery(
  id: string | undefined,
  options?: {
    includeCustomFields?: boolean
    enabled?: boolean
    staleTime?: number
  },
) {
  const { currentOrganization } = useOrganizationStore()
  const queryClient = useQueryClient()

  return useQuery<TaskWithAssignments>({
    queryKey: queryKeys.tasks.detail(id || '', {
      includeCustomFields: options?.includeCustomFields !== false,
    }),
    queryFn: () =>
      tasksApi.getTask(id!, options?.includeCustomFields !== false),
    enabled: options?.enabled !== false && !!id && !!currentOrganization?.id,
    staleTime: options?.staleTime ?? 2 * 60 * 1000, // 2 minutes - task details are stable, show cached data
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch a task by task number
 */
export function useTaskByNumberQuery(
  taskNumber: string | undefined,
  options?: {
    enabled?: boolean
    staleTime?: number
  },
) {
  const { currentOrganization } = useOrganizationStore()

  return useQuery<TaskWithAssignments>({
    queryKey: queryKeys.tasks.byNumber(taskNumber || ''),
    queryFn: () => tasksApi.getTaskByNumber(taskNumber!),
    enabled:
      options?.enabled !== false && !!taskNumber && !!currentOrganization?.id,
    staleTime: options?.staleTime ?? 2 * 60 * 1000, // 2 minutes - task details are stable
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch subtasks for a parent task
 * Note: Uses simple ['subtasks', parentTaskId] key format for consistency
 * with cache operations in SubtasksSection and realtime handlers
 */
export function useSubtasksQuery(
  parentTaskId: string | undefined,
  options?: {
    enabled?: boolean
    staleTime?: number
  },
) {
  const { currentOrganization } = useOrganizationStore()
  const hasPersistedParentTaskId = !!parentTaskId && isValidUUID(parentTaskId)

  return useQuery<TaskWithAssignments[]>({
    // Use simple key format for consistency with cache updates in SubtasksSection
    queryKey: ['subtasks', parentTaskId || ''] as const,
    queryFn: async () => {
      // Use dedicated getSubtasks API method for better performance
      return tasksApi.getSubtasks(parentTaskId!)
    },
    enabled:
      options?.enabled !== false &&
      hasPersistedParentTaskId &&
      !!currentOrganization?.id,
    staleTime: options?.staleTime ?? 2 * 60 * 1000, // 2 minutes default - subtasks change infrequently
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
  })
}

/**
 * Hook to fetch task groups
 */
export function useTaskGroupsQuery(
  filters?: {
    moduleType?: ModuleTypeEnum
    moduleId?: string
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    page?: number
    limit?: number
  },
  options?: {
    enabled?: boolean
  },
) {
  const { currentOrganization } = useOrganizationStore()

  // Default limit to 100 to ensure all groups are fetched
  const filtersWithLimit = filters
    ? { ...filters, limit: filters.limit ?? 100 }
    : { limit: 100 }

  return useQuery({
    queryKey: [
      ...queryKeys.tasks.groups(filtersWithLimit as Record<string, unknown>),
      { source: 'api' },
    ],
    queryFn: async () => {
      return tasksApi.getTaskGroups(filtersWithLimit)
    },
    enabled:
      options?.enabled !== false &&
      !!currentOrganization?.id &&
      !!filters?.moduleType &&
      !!filters?.moduleId,
    placeholderData: previous => previous,
    staleTime: 10 * 60 * 1000, // 10 minutes - groups rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  })
}
